import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CourseGrid from '../components/courses/CourseGrid';
import CourseFilters from '../components/courses/CourseFilters';
import apiService from '../services/api';

const CoursesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [regularCourses, setRegularCourses] = useState([]);
  const [showAllCourses, setShowAllCourses] = useState(false); // New state for "See All" view
  
  // Pagination state for "All Courses" view
  const [currentPage, setCurrentPage] = useState(1);
  const coursesPerPage = 20;
  
  // Bundle selection state
  const [bundleMode, setBundleMode] = useState(false);
  const [selectedForBundle, setSelectedForBundle] = useState([]);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [purchasedCourseIds, setPurchasedCourseIds] = useState([]);
  const [showBundlePaymentModal, setShowBundlePaymentModal] = useState(false);
  const [processingBundlePayment, setProcessingBundlePayment] = useState(false);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  // Function to show notification
  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  // Check for purchase conflicts
  const checkBundleConflicts = async (itemIds) => {
    try {
      // Get user's paid purchases
      const purchases = await apiService.getUserPurchases();
      const paidItemIds = purchases
        .filter(p => p.payment_status === 'paid')
        .map(p => p.course_id || p.id);
      
      // Get user's pending manual payment requests
      const pendingPayments = await apiService.getUserManualPayments();
      const pendingItemIds = [];
      
      pendingPayments.forEach(payment => {
        if (payment.status === 'pending') {
          if (payment.purchase_type === 'bundle' && payment.item_ids) {
            try {
              const ids = JSON.parse(payment.item_ids);
              pendingItemIds.push(...ids);
            } catch (e) {
              console.error('Error parsing item_ids:', e);
            }
          } else if (payment.course_id) {
            pendingItemIds.push(payment.course_id);
          }
        }
      });
      
      // Check for conflicts
      const alreadyOwned = itemIds.filter(id => paidItemIds.includes(id));
      const pendingConflicts = itemIds.filter(id => pendingItemIds.includes(id));
      
      return {
        hasConflict: alreadyOwned.length > 0 || pendingConflicts.length > 0,
        alreadyOwned,
        pendingConflicts
      };
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return { hasConflict: false };
    }
  };

  useEffect(() => {
    loadData();
    // Check if we're in bundle mode from URL parameter
    const params = new URLSearchParams(location.search);
    if (params.get('mode') === 'bundle') {
      setBundleMode(true);
    }
  }, [location]);

  useEffect(() => {
    loadCourses();
  }, [selectedCategory, searchTerm, showAllCourses]); // Add showAllCourses to dependencies

  const loadData = async () => {
    try {
      const categoriesData = await apiService.getCategories();
      setCategories(categoriesData);
      
      // Load user's purchased courses if authenticated
      try {
        const purchases = await apiService.getUserPurchases();
        const purchasedIds = purchases
          .filter(p => p.content_type === 'course')
          .map(p => p.id);
        setPurchasedCourseIds(purchasedIds);
      } catch (err) {
        // User not authenticated or error loading purchases
        console.log('Could not load purchases:', err);
      }
      
      await loadCourses();
    } catch (err) {
      setError('فشل تحميل البيانات. يرجى المحاولة لاحقاً.');
      console.error('Error loading data:', err);
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      setLoading(true);
      const coursesData = await apiService.getCourses(selectedCategory, searchTerm);
      setCourses(coursesData);
      
      // Separate featured and regular courses only when no filters applied AND not in "show all" mode
      if (!selectedCategory && !searchTerm && !showAllCourses) {
        const featured = coursesData.filter(c => c.is_featured === 1);
        const regular = coursesData.filter(c => c.is_featured !== 1);
        setFeaturedCourses(featured);
        setRegularCourses(regular);
      } else {
        setFeaturedCourses([]);
        setRegularCourses(coursesData);
      }
      
      setError(null);
    } catch (err) {
      setError('فشل تحميل الكتب. يرجى المحاولة لاحقاً.');
      console.error('Error loading courses:', err);
    } finally {
      setLoading(false);
    }
  };

  // Bundle selection handlers
  const handleToggleCourseSelection = (course) => {
    // Check if already purchased
    if (purchasedCourseIds.includes(course.id)) {
      showNotification('هذا المحتوى مشترى مسبقاً! اختار محتوى آخر', 'warning');
      return;
    }

    if (selectedForBundle.find(c => c.id === course.id)) {
      // Remove from selection
      setSelectedForBundle(selectedForBundle.filter(c => c.id !== course.id));
    } else {
      // Check if already at max limit (5)
      if (selectedForBundle.length >= 5) {
        showNotification('وصلت للحد الأقصى! يمكنك اختيار 5 محتويات فقط', 'error');
        return;
      }
      // Add to selection
      setSelectedForBundle([...selectedForBundle, course]);
      showNotification(`تمت الإضافة! (${selectedForBundle.length + 1}/5)`, 'success');
    }
  };

  const handleProceedToCheckout = async () => {
    if (selectedForBundle.length >= 2) {
      setShowBundlePaymentModal(true);
    }
  };

  const handleBundleOnlinePayment = async () => {
    try {
      setProcessingBundlePayment(true);
      const courseIds = selectedForBundle.map(c => c.id);
      const { checkoutUrl } = await apiService.createBundleCheckout(courseIds);
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('Bundle checkout error:', err);
      showNotification('عذراً! حدث خطأ. يرجى المحاولة مرة أخرى أو التواصل معنا', 'error');
    } finally {
      setProcessingBundlePayment(false);
    }
  };

  // Calculate bundle pricing from actual course prices
  const calculateRealBundlePricing = () => {
    if (selectedForBundle.length < 2) return null;
    
    // Calculate total from actual course prices
    const originalTotal = selectedForBundle.reduce((sum, course) => sum + (course.price || 0), 0);
    
    // Get discount percentage based on quantity
    let discountPercent = 0;
    if (selectedForBundle.length === 2) discountPercent = 10;
    else if (selectedForBundle.length === 3) discountPercent = 15;
    else if (selectedForBundle.length === 4) discountPercent = 20;
    else if (selectedForBundle.length >= 5) discountPercent = 25;
    
    const discountAmount = Math.round(originalTotal * (discountPercent / 100));
    const finalPrice = originalTotal - discountAmount;
    
    return {
      original: originalTotal,
      final: finalPrice,
      discount: discountPercent,
      discountAmount: discountAmount
    };
  };

  const bundlePricing = calculateRealBundlePricing();

  // Pagination calculations for "All Courses" view
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = showAllCourses ? regularCourses.slice(indexOfFirstCourse, indexOfLastCourse) : regularCourses;
  const totalPages = showAllCourses ? Math.ceil(regularCourses.length / coursesPerPage) : 0;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="relative z-10 py-20 bg-gradient-to-b from-bg to-white min-h-screen overflow-x-hidden">
      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] animate-fade-in">
          <div className={`px-6 py-4 rounded-2xl shadow-2xl font-bold text-white flex items-center gap-3 ${
            notification.type === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
            notification.type === 'error' ? 'bg-gradient-to-r from-red-500 to-pink-600' :
            notification.type === 'warning' ? 'bg-gradient-to-r from-orange-500 to-yellow-500' :
            'bg-gradient-to-r from-blue-500 to-indigo-600'
          }`}>
            <i className={`fas ${
              notification.type === 'success' ? 'fa-check-circle' :
              notification.type === 'error' ? 'fa-times-circle' :
              notification.type === 'warning' ? 'fa-exclamation-triangle' :
              'fa-info-circle'
            } text-2xl`}></i>
            <span className="text-lg">{notification.message}</span>
          </div>
        </div>
      )}

      {/* Bundle Mode Floating Cart */}
      {bundleMode && (
        <div className="fixed bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4">
          <div className="bg-gradient-to-br from-primary to-primary-dark text-white rounded-2xl md:rounded-3xl shadow-2xl p-4 md:p-6">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div>
                <h3 className="text-lg md:text-2xl font-black mb-1 flex items-center gap-2">
                  <i className="fas fa-boxes text-base md:text-xl"></i> حزمتك المخصصة
                </h3>
                <p className="text-xs md:text-sm text-white/90">اختار من 2 حتى 5 محتويات</p>
              </div>
              <button
                onClick={() => {
                  setBundleMode(false);
                  setSelectedForBundle([]);
                  navigate('/courses');
                }}
                className="text-white/80 hover:text-white transition-colors"
              >
                <i className="fas fa-times text-xl md:text-2xl"></i>
              </button>
            </div>
            
            {selectedForBundle.length > 0 ? (
              <>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 mb-3 md:mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-sm md:text-base">{selectedForBundle.length} محتويات محددة</span>
                    {bundlePricing && (
                      <span className="text-xs md:text-sm bg-green-500 px-2 py-0.5 md:px-3 md:py-1 rounded-full">خصم {bundlePricing.discount}%</span>
                    )}
                  </div>
                  {bundlePricing && (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs md:text-sm line-through text-white/70">{bundlePricing.original} دج</div>
                        <div className="text-xl md:text-3xl font-black">{bundlePricing.final} دج</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs md:text-sm text-white/90">وفرت</div>
                        <div className="text-base md:text-xl font-bold text-green-300">{bundlePricing.discountAmount} دج</div>
                      </div>
                    </div>
                  )}
                </div>
                
                {selectedForBundle.length >= 2 ? (
                  <button
                    onClick={handleProceedToCheckout}
                    className="w-full bg-white text-primary px-4 py-3 md:px-6 md:py-4 rounded-xl md:rounded-2xl font-black text-base md:text-lg hover:bg-gray-100 transition-all duration-300 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    إتمام الشراء
                    <i className="fas fa-arrow-left mr-2"></i>
                  </button>
                ) : (
                  <div className="text-center text-white/90 text-xs md:text-sm">
                    اختار محتوى واحد على الأقل للمتابعة
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-white/90 text-sm md:text-base">
                ابدأ باختيار المحتوى اللي تحبو!
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bundle Mode Banner */}
      {bundleMode && (
        <div className="bg-gradient-to-r from-primary to-primary-dark text-white py-4 mb-8">
          <div className="container mx-auto px-4 text-center">
            <p className="text-lg font-bold flex items-center justify-center gap-2">
              <i className="fas fa-boxes"></i> وضع الحزمة المخصصة مفعّل - اختار حتى 5 محتويات واحصل على خصم حتى 25%!
            </p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold uppercase tracking-wider text-primary bg-white px-4 py-2 rounded-full mb-4 shadow-md">
              منصة التعليم بالدارجة الجزائرية
            </span>
            <h1 className="text-4xl md:text-5xl font-black text-text mb-4">
              الدورات الرقمية التفاعلية
            </h1>
            <p className="text-xl text-gray-600 max-w-[700px] mx-auto">
              تعلم بطريقة عصرية: فيديو توضيحي + محتوى مكتوب + عرض تقديمي + بطاقات تعليمية + ملخص مرئي
            </p>
          </div>

          {/* Learning Path Section - Only show in normal view, NOT in "show all" mode */}
          {!showAllCourses && (
            <div className="mb-14 bg-white rounded-3xl shadow-custom-lg border-2 border-gray-100 p-8 sm:p-10 overflow-hidden relative">
            {/* Subtle blue gradient blobs - matching hero */}
            <div className="absolute top-0 left-0 w-40 h-40 md:w-56 md:h-56 lg:w-64 lg:h-64 bg-primary/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-48 h-48 md:w-60 md:h-60 lg:w-72 lg:h-72 bg-blue-400/10 rounded-full blur-3xl"></div>
            
            {/* Header */}
            <div className="text-center mb-10 relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full mb-4 font-semibold text-sm">
                <i className="fas fa-route"></i>
                <span>كيف تتعلم معنا</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-black text-text mb-3 leading-tight">
                مسار التعلم في كل دورة
              </h3>
              <p className="text-base md:text-lg text-text2 max-w-2xl mx-auto">
                خمس مراحل منظمة تأخذك من الصفر إلى الإتقان
              </p>
            </div>

            {/* Desktop flowchart with arrows */}
            <div className="hidden lg:block relative">
              <div className="flex justify-between items-start relative px-4">
                {[
                  { num: '1', icon: 'fas fa-video', title: 'فيديو توضيحي', color: 'from-purple-500 to-pink-500' },
                  { num: '2', icon: 'fas fa-book-open', title: 'محتوى مكتوب', color: 'from-blue-500 to-indigo-600' },
                  { num: '3', icon: 'fas fa-chalkboard-teacher', title: 'عرض تقديمي', color: 'from-green-500 to-emerald-500' },
                  { num: '4', icon: 'fas fa-layer-group', title: 'بطاقات تعليمية', color: 'from-orange-500 to-red-500' },
                  { num: '5', icon: 'fas fa-file-image', title: 'ملخص مرئي', color: 'from-cyan-500 to-blue-500' },
                ].map((step, i, arr) => (
                  <div key={i} className="flex items-center" style={{ width: i === arr.length - 1 ? 'calc(20% - 30px)' : '20%' }}>
                    <div className="flex flex-col items-center w-full group cursor-pointer">
                      {/* Circle */}
                      <div className="relative z-10">
                        <div
                          className={`relative w-20 h-20 bg-gradient-to-br ${step.color} rounded-2xl flex flex-col items-center justify-center shadow-xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 border-2 border-white`}
                        >
                          <i className={`${step.icon} text-white text-2xl drop-shadow-lg`}></i>
                        </div>
                        {/* Glow effect */}
                        <div className={`absolute inset-0 w-20 h-20 bg-gradient-to-br ${step.color} rounded-2xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity`}></div>
                      </div>
                      
                      {/* Content */}
                      <div className="text-center mt-4">
                        <h4 className="font-black text-text text-sm group-hover:text-primary transition-colors">{step.title}</h4>
                      </div>
                    </div>
                    
                    {/* Arrow between steps - RTL (Right to Left) */}
                    {i < arr.length - 1 && (
                      <div className="flex items-center justify-center mx-3 shrink-0">
                        <div className="flex flex-col items-center">
                          <svg width="40" height="20" viewBox="0 0 40 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path 
                              d="M38 10 L10 10 M10 10 L16 5 M10 10 L16 15" 
                              stroke="#cbd5e1" 
                              strokeWidth="2.5" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile/Tablet grid layout */}
            <div className="lg:hidden relative">
              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                {[
                  { num: '1', icon: 'fas fa-video', title: 'فيديو توضيحي', color: 'from-purple-500 to-pink-500' },
                  { num: '2', icon: 'fas fa-book-open', title: 'محتوى مكتوب', color: 'from-blue-500 to-indigo-600' },
                  { num: '3', icon: 'fas fa-chalkboard-teacher', title: 'عرض تقديمي', color: 'from-green-500 to-emerald-500' },
                  { num: '4', icon: 'fas fa-layer-group', title: 'بطاقات تعليمية', color: 'from-orange-500 to-red-500' },
                  { num: '5', icon: 'fas fa-file-image', title: 'ملخص مرئي', color: 'from-cyan-500 to-blue-500' },
                ].map((step, i) => (
                  <div 
                    key={i}
                    className={i === 4 ? 'col-span-2' : ''}
                  >
                    <div className="bg-white rounded-2xl p-4 border-2 border-gray-100 hover:border-primary/30 transition-all duration-300 hover:shadow-lg h-full">
                      <div className="flex flex-col items-center justify-center gap-2 relative group">
                        <div
                          className={`w-14 h-14 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center relative z-10 shadow-lg border-2 border-white transform group-hover:scale-110 transition-all duration-300`}
                        >
                          <i className={`${step.icon} text-white text-xl`}></i>
                        </div>
                        <div className="text-center">
                          <span className={`inline-block px-2 py-0.5 bg-gray-100 text-xs font-bold rounded-full mb-1`}>
                            {step.num}
                          </span>
                          <h4 className="font-black text-text text-sm">{step.title}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          )}

          <CourseFilters
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />

          {loading && (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
              <p className="mt-4 text-gray-600 text-lg">جاري التحميل...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl mb-8">
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Featured Courses Section - Only show when no filters AND not showing all */}
              {featuredCourses.length > 0 && !selectedCategory && !searchTerm && !showAllCourses && (
                <div className="mb-16">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full shadow-lg">
                      <i className="fas fa-fire"></i>
                      <span className="font-black text-sm">الدورات الأكثر طلباً</span>
                    </div>
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-yellow-400/30 to-transparent"></div>
                  </div>
                  <CourseGrid 
                    courses={featuredCourses}
                    bundleMode={bundleMode}
                    selectedForBundle={selectedForBundle}
                    onToggleSelection={handleToggleCourseSelection}
                    purchasedCourseIds={purchasedCourseIds}
                    isFeatured={true}
                  />
                </div>
              )}

              {/* Regular/All Courses Section */}
              {regularCourses.length > 0 && (
                <>
                  {/* Show header when appropriate */}
                  {((featuredCourses.length > 0 && !selectedCategory && !searchTerm && !showAllCourses) || showAllCourses) && (
                    <div className="flex items-center gap-3 mb-8">
                      <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full">
                        <i className="fas fa-book-open"></i>
                        <span className="font-black text-sm">
                          {showAllCourses ? `جميع الدورات المتاحة (${regularCourses.length})` : 'جميع الدورات'}
                        </span>
                      </div>
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-primary/20 to-transparent"></div>
                      
                      {/* "See All" button when NOT in "show all" mode */}
                      {!showAllCourses && !selectedCategory && !searchTerm && (
                        <button
                          onClick={() => {
                            setShowAllCourses(true);
                            setCurrentPage(1);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-full transition-all text-sm font-bold hover:scale-105 transform"
                        >
                          <span>استعرض الكل</span>
                          <i className="fas fa-arrow-left"></i>
                        </button>
                      )}
                      
                      {/* "Back" button when IN "show all" mode */}
                      {showAllCourses && (
                        <button
                          onClick={() => {
                            setShowAllCourses(false);
                            setCurrentPage(1); // Reset pagination
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-text rounded-full transition-colors text-sm font-bold"
                        >
                          <i className="fas fa-arrow-right"></i>
                          <span>العودة</span>
                        </button>
                      )}
                    </div>
                  )}
                  <CourseGrid 
                    courses={currentCourses}
                    bundleMode={bundleMode}
                    selectedForBundle={selectedForBundle}
                    onToggleSelection={handleToggleCourseSelection}
                    purchasedCourseIds={purchasedCourseIds}
                  />
                  
                  {/* Pagination - Only show when in "show all" mode */}
                  {showAllCourses && totalPages > 1 && (
                    <div className="mt-12 flex justify-center">
                      <div className="inline-flex items-center gap-2 bg-white rounded-2xl p-2 shadow-lg border-2 border-gray-100">
                        {/* Previous Button */}
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`px-4 py-2 rounded-xl font-bold transition-all ${
                            currentPage === 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-primary hover:bg-primary/10'
                          }`}
                        >
                          <i className="fas fa-arrow-right"></i>
                          <span className="mr-2">السابق</span>
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center gap-1">
                          {[...Array(totalPages)].map((_, index) => {
                            const pageNumber = index + 1;
                            // Show first page, last page, current page, and pages around current
                            const showPage = 
                              pageNumber === 1 ||
                              pageNumber === totalPages ||
                              (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);
                            
                            // Show ellipsis
                            const showEllipsis =
                              (pageNumber === currentPage - 2 && currentPage > 3) ||
                              (pageNumber === currentPage + 2 && currentPage < totalPages - 2);

                            if (showEllipsis) {
                              return (
                                <span key={pageNumber} className="px-2 text-gray-400">
                                  ...
                                </span>
                              );
                            }

                            if (!showPage) return null;

                            return (
                              <button
                                key={pageNumber}
                                onClick={() => handlePageChange(pageNumber)}
                                className={`w-10 h-10 rounded-xl font-bold transition-all ${
                                  currentPage === pageNumber
                                    ? 'bg-primary text-white shadow-lg'
                                    : 'text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          })}
                        </div>

                        {/* Next Button */}
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`px-4 py-2 rounded-xl font-bold transition-all ${
                            currentPage === totalPages
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-primary hover:bg-primary/10'
                          }`}
                        >
                          <span className="ml-2">التالي</span>
                          <i className="fas fa-arrow-left"></i>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* See All Courses CTA - Only show when NOT in "show all" mode and no filters */}
                  {!selectedCategory && !searchTerm && !showAllCourses && (
                    <div className="mt-16 text-center">
                      <div className="max-w-3xl mx-auto bg-gradient-to-br from-primary via-primary-dark to-secondary rounded-3xl p-6 md:p-10 lg:p-12 shadow-2xl relative overflow-hidden">
                        {/* Background decoration - matching pricing page */}
                        <div className="absolute inset-0 opacity-20">
                          <div
                            className="absolute inset-0"
                            style={{
                              backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.2) 0%, transparent 50%)`
                            }}
                          ></div>
                        </div>
                        
                        <div className="relative z-10">
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 text-white rounded-full mb-4 backdrop-blur-sm">
                            <i className="fas fa-book-open"></i>
                            <span className="font-bold text-sm">مكتبة متكاملة</span>
                          </div>
                          
                          <h3 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight">
                            اكتشف جميع الدورات
                          </h3>
                          
                          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
                            استكشف مكتبتنا الكاملة من الدورات التعليمية في مختلف المجالات - أعمال، لغات، تعليم الأطفال، علوم إسلامية وأكثر
                          </p>
                          
                          <button
                            onClick={() => {
                              setShowAllCourses(true);
                              setCurrentPage(1); // Reset to first page
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="inline-flex items-center gap-3 bg-white text-primary px-8 py-4 rounded-xl font-black text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl"
                          >
                            <span>استعرض جميع الدورات</span>
                            <i className="fas fa-arrow-left"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* No courses found */}
              {courses.length === 0 && (
                <div className="text-center py-20">
                  <i className="fas fa-book-open text-6xl text-gray-300 mb-4"></i>
                  <p className="text-xl text-gray-600">لا توجد دورات متاحة حالياً</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Bundle Payment Modal */}
      {showBundlePaymentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl md:text-3xl font-black text-text">
                  <i className="fas fa-boxes text-primary ml-2"></i>
                  حزمتك المخصصة
                </h3>
                <button
                  onClick={() => setShowBundlePaymentModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times text-2xl"></i>
                </button>
              </div>

              <div className="bg-gray-50 p-6 rounded-2xl mb-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">المحتويات المحددة:</span>
                  <span className="font-bold text-gray-800">{selectedForBundle.length} محتويات</span>
                </div>
                <div className="space-y-2 mb-4">
                  {selectedForBundle.map((course) => (
                    <div key={course.id} className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">{course.title_ar}</span>
                      <span className="font-bold">{course.price} دج</span>
                    </div>
                  ))}
                </div>
                {bundlePricing && (
                  <>
                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">المجموع الأصلي:</span>
                        <span className="font-bold line-through text-gray-400">{bundlePricing.original} دج</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-green-600 font-bold">الخصم ({bundlePricing.discount}%):</span>
                        <span className="font-bold text-green-600">-{bundlePricing.discountAmount} دج</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t">
                        <span className="text-gray-600 font-black text-lg">المجموع النهائي:</span>
                        <span className="font-black text-primary text-3xl">{bundlePricing.final} دج</span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="mb-6">
                <button
                  onClick={handleBundleOnlinePayment}
                  disabled={processingBundlePayment}
                  className="w-full bg-primary text-white px-6 py-4 rounded-xl font-bold hover:bg-primary-dark transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <i className="fas fa-credit-card"></i>
                  {processingBundlePayment ? 'جاري التحويل للدفع...' : 'دفع أونلاين'}
                </button>
                <p className="text-xs text-gray-600 text-center mt-2">
                  سيتم تحويلك لصفحة الدفع الآمنة (CIB / EDAHABIA)
                </p>
              </div>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-4 text-sm text-gray-400">أو</span>
                </div>
              </div>

              <div>
                <button
                  onClick={async () => {
                    setCheckingConflicts(true);
                    const itemIds = selectedForBundle.map(c => c.id);
                    const conflicts = await checkBundleConflicts(itemIds);
                    setCheckingConflicts(false);
                    
                    if (conflicts.hasConflict) {
                      const messages = [];
                      if (conflicts.alreadyOwned.length > 0) {
                        messages.push(`لقد اشتريت بالفعل ${conflicts.alreadyOwned.length} من المحتويات في هذه الحزمة`);
                      }
                      if (conflicts.pendingConflicts.length > 0) {
                        messages.push(`لديك ${conflicts.pendingConflicts.length} طلب دفع يدوي قيد المراجعة لمحتويات في هذه الحزمة`);
                      }
                      showNotification(
                        `${messages.join(' و ')}. لا يمكنك شراء حزمة تحتوي على محتويات تمتلكها أو قيد المراجعة.`,
                        'error'
                      );
                      return;
                    }
                    
                    setShowBundlePaymentModal(false);
                    navigate(`/manual-payment/bundle?items=${itemIds.join(',')}`);
                  }}
                  disabled={checkingConflicts}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-xl font-bold hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <i className="fas fa-money-bill-wave"></i>
                  {checkingConflicts ? 'جاري التحقق...' : 'الدفع اليدوي عبر CCP (حوالة بريدية)'}
                </button>
                <p className="text-xs text-gray-600 text-center mt-2">
                  للدفع عبر الحساب الجاري البريدي
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursesPage;
